import moment from 'moment';
import { useCallback, useState } from 'react';
import Badge from 'react-bootstrap/Badge';
import Button from 'react-bootstrap/Button';
import Card from 'react-bootstrap/Card';
import classNames from 'classnames';
// API
import { useAuth } from '../../../../../hooks/useAuth';
import { useEnv } from '../../../../../context/env-context';
import { makeRequest } from '../../../../../utils/makeRequest';
import { toast } from 'react-toastify';
// constants
import {
  CARD_ACTIVATION_STATUS,
  CARD_APPROVAL_RESPONSE_MESSAGES,
  CARD_FRIENDLY_STATUS,
  CARD_VALIDATION_STATUS,
  CARD_VERIFICATION_RESPONSE_MESSAGES,
} from '../../../../../constants/consumers';
// icons
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faStar, faTrashAlt } from '@fortawesome/free-solid-svg-icons';
import { faCheckCircle, faXmarkCircle } from '@fortawesome/free-regular-svg-icons';
import VerifiedUserOutlinedIcon from '@mui/icons-material/VerifiedUserOutlined';
import GppBadOutlinedIcon from '@mui/icons-material/GppBadOutlined';
// components
import CardProviderImage from '../../../../../components/CardProviderImage';
import CommonSelectInput from '../../../../../components/common/CommonSelectInput';
// forms
import ConsumerPopoverNewCardForm from '../forms/ConsumerPopoverNewCardForm';

function ConsumerTabaPayCardsSection({
  tabaPayCards,
  getCards,
  getAstraUser,
  sendCardRequest,
  getBankAccountValue,
  cardsBlockStatus,
  getCardsBlockStatus,
  unblockLinkStatus,
  bankAccounts,
  consumerId,
  consumerFirstName,
  consumerLastName,
  consumerAddressLine1,
  consumerAddressLine2,
  consumerCity,
  consumerState,
  consumerZip,
  consumerManualCardValidation,
  consumerAllowAdHocCardException,
  setConsumer,
}) {
  const { apiOriginConsumer } = useEnv();
  const { getAccessTokenSilently } = useAuth();

  const [cardBottomOpen, setCardBottomOpen] = useState({});

  const updateConsumer = useCallback(
    async fields => {
      const token = await getAccessTokenSilently();

      if (!token) {
        return;
      }

      const config = {
        token,
        url: `${apiOriginConsumer}/update_consumer`,
        method: 'POST',
        data: JSON.stringify({ consumerId, ...fields }),
      };

      try {
        const response = await makeRequest(config);

        if (response.status === 'ok') {
          setConsumer(prevConsumer => ({ ...prevConsumer, ...fields }));
        }
      } catch (error) {
        toast.error(error.message);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [getAccessTokenSilently, apiOriginConsumer],
  );

  const getBankAccountValueNew = card => {
    const mainAccount = bankAccounts?.find(x => x.isMain);

    if (card.pushVerificationStatus === 'NOT_VERIFIED') {
      return (
        <div className="d-flex align-items-center gap-2 bo-min-w-0">
          <Badge className="bo-common-inline-badge" bg="secondary">
            Not Verified
          </Badge>
          <Button
            className="bo-common-inline-button p-0 text-truncate"
            onClick={() =>
              sendCardRequest({
                url: 'verification/init',
                data: { cardId: card.cardId },
                onSuccess: response => {
                  if (CARD_VERIFICATION_RESPONSE_MESSAGES[response.verificationResponse]) {
                    toast.success(
                      CARD_VERIFICATION_RESPONSE_MESSAGES[response.verificationResponse],
                    );
                  } else if (response.verificationResponse === 'FAILED') {
                    toast.error('Verification initiation failed');
                  }
                },
              })
            }
          >
            Verify
          </Button>
        </div>
      );
    }

    if (card.pushVerificationStatus === 'IN_PROGRESS') {
      return (
        <div className="d-flex align-items-center gap-2 bo-min-w-0">
          <Badge className="bo-common-inline-badge" bg="info">
            Verification in progress
          </Badge>
          <Button
            className="bo-common-inline-button p-0 text-truncate"
            onClick={() =>
              sendCardRequest({
                url: 'verification/match',
                data: { cardId: card.cardId, accountId: mainAccount.id },
              })
            }
          >
            Approve
          </Button>
          <Button
            className="bo-common-inline-button p-0 text-truncate"
            onClick={() =>
              sendCardRequest({
                url: 'verification/cancel',
                data: { cardId: card.cardId },
                askForConfirmation: true,
              })
            }
          >
            Cancel
          </Button>
        </div>
      );
    }

    if (card.pushVerificationStatus === 'VERIFIED') {
      const accountToken = card?.currentVerification?.verifiedAccountToken || null;
      const accountName = card?.currentVerification?.verifiedAccountName || '---';
      const accountMask = card?.currentVerification?.verifiedAccountMask || 'XXXX';

      return (
        <div className="d-flex flex-column bo-min-w-0">
          <div className="d-flex align-items-center gap-2">
            <FontAwesomeIcon
              icon={mainAccount?.achToken === accountToken ? faCheckCircle : faXmarkCircle}
              color={mainAccount?.achToken === accountToken ? 'green' : 'red'}
              size="sm"
            />
            <p
              className={classNames(
                'bo-common-text text-truncate',
                mainAccount?.achToken !== accountToken && 'bo-text-red',
              )}
            >
              {`${accountName} ${accountMask}`}
            </p>
          </div>
          {mainAccount?.achToken !== accountToken && (
            <p className="bo-common-text-xs text-truncate">
              Verified account is no longer the main account
            </p>
          )}
        </div>
      );
    }

    if (card.pushVerificationStatus === 'FAILED') {
      return (
        <div className="d-flex align-items-center gap-2 bo-min-w-0">
          <Badge className="bo-common-inline-badge" bg="danger">
            Verification failed
          </Badge>
          <Button
            className="bo-common-inline-button p-0 text-truncate"
            onClick={() =>
              sendCardRequest({
                url: 'verification/init',
                data: { cardId: card.cardId },
                onSuccess: response => {
                  if (CARD_VERIFICATION_RESPONSE_MESSAGES[response.verificationResponse]) {
                    toast.success(
                      CARD_VERIFICATION_RESPONSE_MESSAGES[response.verificationResponse],
                    );
                  } else if (response.verificationResponse === 'FAILED') {
                    toast.error('Verification initiation failed');
                  }
                },
              })
            }
          >
            Verify
          </Button>
        </div>
      );
    }

    return (
      <div className="d-flex align-items-center">
        <Badge className="bo-common-inline-badge" bg="warning">
          Unknown
        </Badge>
      </div>
    );
  };

  return (
    <div>
      <div className="d-flex align-items-center gap-1">
        <ConsumerPopoverNewCardForm
          buttonTitle="Link New Card"
          getAstraUser={getAstraUser}
          getCards={getCards}
          cardsBlockStatus={cardsBlockStatus}
          getCardsBlockStatus={getCardsBlockStatus}
          consumerId={consumerId}
          consumerFirstName={consumerFirstName}
          consumerLastName={consumerLastName}
          consumerAddressLine1={consumerAddressLine1}
          consumerAddressLine2={consumerAddressLine2}
          consumerCity={consumerCity}
          consumerState={consumerState}
          consumerZip={consumerZip}
        />
      </div>
      {cardsBlockStatus.blocked && (
        <div className="d-flex align-items-end gap-2 mt-2">
          <p className="bo-common-text-sm text-danger text-nowrap text-truncate">
            Blocked for linking a new card until{' '}
            {moment.utc(cardsBlockStatus.blockedUntil).local().format('h:mm a')}
          </p>
          <Button className="bo-common-inline-button text-nowrap" onClick={unblockLinkStatus}>
            <p className="bo-common-text-bold">Unblock</p>
          </Button>
        </div>
      )}
      <div>
        <CommonSelectInput
          label="Manual Card Validation"
          initialValue={consumerManualCardValidation ? 'Yes' : 'No'}
          labelClassName="mt-3"
          options={[
            { key: 'Yes', value: 'Yes', name: 'Yes' },
            { key: 'No', value: 'No', name: 'No' },
          ]}
          onSave={value => updateConsumer({ manualCardValidation: value === 'Yes' })}
        />
        <CommonSelectInput
          label="Allow AdHoc Card Exception"
          initialValue={consumerAllowAdHocCardException ? 'Yes' : 'No'}
          labelClassName="mt-3"
          options={[
            { key: 'Yes', value: 'Yes', name: 'Yes' },
            { key: 'No', value: 'No', name: 'No' },
          ]}
          onSave={value => updateConsumer({ allowAdHocCardException: value === 'Yes' })}
        />
      </div>
      <div className="mt-3">
        {tabaPayCards.map(card => (
          <Card className="mb-3 p-3 bo-max-w-450 overflow-hidden" key={card.cardId}>
            <div className="d-flex justify-content-between align-items-center">
              <CardProviderImage height={12} company={card.company} />
              <div className="d-flex align-items-center">
                <p className="bo-common-text me-1">{card.type?.toUpperCase()}</p>
                {card.isMain && <FontAwesomeIcon icon={faStar} size="sm" color="orange" />}
                {card.adhocStatus !== 'VALID' && !card.isMain && (
                  <Button
                    className="bo-common-inline-button ms-1"
                    onClick={() =>
                      sendCardRequest({
                        url: 'set_main',
                        data: { consumerId, cardId: card.cardId },
                      })
                    }
                  >
                    Set Main
                  </Button>
                )}
              </div>
            </div>
            <div className="mt-3">
              <div className="d-flex mb-1 align-items-center">
                <p className="bo-common-text bo-w-150">Card Number:</p>
                <p className="bo-common-text text-nowrap">
                  {`**** ***** **** ${card.last4Digits}`}
                </p>
              </div>
              <div className="d-flex mb-1 align-items-center">
                <p className="bo-common-text bo-w-150">EXP:</p>
                <p className={`bo-common-text ${card.isExpired ? 'bo-text-red' : ''}`}>
                  {`${card.expiration} ${card.isExpired ? 'Expired!' : ''}`}
                </p>
              </div>
              <div className="d-flex mb-1 align-items-center">
                <p className="bo-common-text bo-min-w-150">Card Holder:</p>
                <p className="bo-common-text text-truncate">
                  {`${card.firstName} ${card.lastName}`}
                </p>
              </div>
              <hr className="my-1" />
              <div className="d-flex mb-1 align-items-top overflow-hidden">
                <p className="bo-common-text bo-min-w-150">Status:</p>
                <div className="d-flex align-items-center gap-2 w-100">
                  <Badge
                    className="bo-common-inline-badge"
                    bg={CARD_FRIENDLY_STATUS[card.friendlyStatus]?.color}
                  >
                    {CARD_FRIENDLY_STATUS[card.friendlyStatus]?.text}
                  </Badge>
                </div>
              </div>
              <hr className="my-1" />
              <div className="d-flex mb-1 align-items-top overflow-hidden">
                <p className="bo-common-text bo-min-w-150">Activation:</p>
                <div className="d-flex align-items-center gap-2 w-100">
                  <Badge
                    className="bo-common-inline-badge"
                    bg={CARD_ACTIVATION_STATUS[card.activationStatus]?.color}
                  >
                    {CARD_ACTIVATION_STATUS[card.activationStatus]?.text}
                  </Badge>
                  {card.activationStatus === 'ACTIVE' && (
                    <Button
                      className="bo-common-inline-button p-0 text-nowrap"
                      onClick={() =>
                        sendCardRequest({ url: 'block', data: { cardId: card.cardId } })
                      }
                    >
                      Block
                    </Button>
                  )}
                  {card.activationStatus === 'NOT_ACTIVE' && (
                    <Button
                      className="bo-common-inline-button p-0 text-nowrap"
                      onClick={() =>
                        sendCardRequest({ url: 'activate', data: { cardId: card.cardId } })
                      }
                    >
                      Activate
                    </Button>
                  )}
                  {card.activationStatus === 'BLOCKED' && (
                    <Button
                      className="bo-common-inline-button p-0 text-nowrap"
                      onClick={() =>
                        sendCardRequest({ url: 'unblock', data: { cardId: card.cardId } })
                      }
                    >
                      Unblock
                    </Button>
                  )}
                </div>
              </div>
              <div className="d-flex mb-1 align-items-top overflow-hidden">
                <p className="bo-common-text bo-min-w-150">Validation:</p>
                <div className="d-flex align-items-center gap-2 w-100">
                  <Badge
                    className="bo-common-inline-badge"
                    bg={CARD_VALIDATION_STATUS[card.validationStatus]?.color}
                  >
                    {CARD_VALIDATION_STATUS[card.validationStatus]?.text}
                    {card.manualValidationStatus ? ' (manual)' : ''}
                  </Badge>
                  {card.validationStatus === 'VALID' && card.manualValidationStatus && (
                    <Button
                      className="bo-common-inline-button p-0 text-nowrap"
                      onClick={() =>
                        sendCardRequest({ url: 'reject_valid', data: { cardId: card.cardId } })
                      }
                    >
                      Reject
                    </Button>
                  )}
                  {card.validationStatus === 'INVALID' && card.manualValidationStatus && (
                    <Button
                      className="bo-common-inline-button p-0 text-nowrap"
                      onClick={() =>
                        sendCardRequest({ url: 'approve_invalid', data: { cardId: card.cardId } })
                      }
                    >
                      Approve
                    </Button>
                  )}
                  {card.validationStatus === 'PENDING_APPROVAL' && (
                    <>
                      <Button
                        className="bo-common-inline-button p-0 text-nowrap"
                        onClick={() =>
                          sendCardRequest({
                            url: 'approve_pending',
                            data: { cardId: card.cardId },
                            onSuccess: response => {
                              if (CARD_APPROVAL_RESPONSE_MESSAGES[response.verificationResponse]) {
                                toast.success(
                                  CARD_APPROVAL_RESPONSE_MESSAGES[response.verificationResponse],
                                );
                              }
                            },
                          })
                        }
                      >
                        Approve
                      </Button>
                      <Button
                        className="bo-common-inline-button p-0 text-nowrap"
                        onClick={() =>
                          sendCardRequest({ url: 'reject_pending', data: { cardId: card.cardId } })
                        }
                      >
                        Reject
                      </Button>
                    </>
                  )}
                </div>
              </div>
              <div className="d-flex mb-1 align-items-top overflow-hidden">
                <p className="bo-common-text bo-min-w-150">Usage:</p>
                <div className="d-flex flex-column gap-1">
                  {card.pull === 'ALLOWED' && (
                    <Badge
                      className="bo-common-inline-badge bo-w-60 d-flex align-items-center"
                      bg={
                        !card.pullVerificationStatus || card.pullVerificationStatus === 'FAILED'
                          ? 'danger'
                          : 'success'
                      }
                    >
                      Pull
                      {card.pullVerificationStatus === 'VERIFIED' && (
                        <VerifiedUserOutlinedIcon fontSize="inherit" className="ms-auto" />
                      )}
                      {card.pullVerificationStatus === 'FAILED' && (
                        <GppBadOutlinedIcon fontSize="inherit" className="ms-auto" />
                      )}
                    </Badge>
                  )}
                  {card.push === 'ALLOWED' && (
                    <Badge
                      className="bo-common-inline-badge bo-w-60 d-flex align-items-center"
                      bg={
                        !card.pushVerificationStatus || card.pushVerificationStatus === 'FAILED'
                          ? 'danger'
                          : 'success'
                      }
                    >
                      Push
                      {card.pushVerificationStatus === 'VERIFIED' && (
                        <VerifiedUserOutlinedIcon fontSize="inherit" className="ms-auto" />
                      )}
                      {card.pushVerificationStatus === 'FAILED' && (
                        <GppBadOutlinedIcon fontSize="inherit" className="ms-auto" />
                      )}
                    </Badge>
                  )}
                  {card.adhocStatus === 'VALID' && (
                    <Badge
                      className="bo-common-inline-badge d-flex align-items-center"
                      bg="secondary"
                    >
                      Ad Hoc
                    </Badge>
                  )}
                </div>
              </div>
              <div className="d-flex mb-1 align-items-top">
                <p className="bo-common-text bo-min-w-150">Bank Account:</p>
                {getBankAccountValueNew(card)}
              </div>
              <div className="d-flex mb-1 align-items-center">
                <p className="bo-common-text bo-min-w-150">Bank Account (old):</p>
                {getBankAccountValue(card)}
              </div>
            </div>
            <div className="mt-2 d-flex align-items-center justify-content-between gap-2">
              <div className="d-flex gap-2 bo-min-w-0">
                <Button
                  className="bo-common-inline-button text-nowrap"
                  onClick={() =>
                    setCardBottomOpen(prev => ({
                      ...prev,
                      [card.cardId]: prev[card.cardId] === 'LOG' ? '' : 'LOG',
                    }))
                  }
                >
                  {cardBottomOpen[card.cardId] === 'LOG' ? 'Hide Log' : 'Show Log'}
                </Button>
                <Button
                  className="bo-common-inline-button text-truncate"
                  onClick={() =>
                    setCardBottomOpen(prev => ({
                      ...prev,
                      [card.cardId]: prev[card.cardId] === 'INFO' ? '' : 'INFO',
                    }))
                  }
                >
                  {cardBottomOpen[card.cardId] === 'INFO' ? 'Hide More Info' : 'Show More Info'}
                </Button>
              </div>
              <div className="d-flex gap-2">
                <Button
                  className="bo-common-inline-button text-nowrap"
                  onClick={() =>
                    sendCardRequest({ url: 'validate', data: { cardId: card.cardId } })
                  }
                >
                  Validate
                </Button>
                <Button
                  className="bo-common-base-button"
                  onClick={() =>
                    sendCardRequest({
                      url: 'remove',
                      data: { consumerId, cardId: card.cardId },
                      askForConfirmation: true,
                    })
                  }
                >
                  <FontAwesomeIcon icon={faTrashAlt} color="#5946A4" size="sm" />
                </Button>
              </div>
            </div>
            {cardBottomOpen[card.cardId] === 'LOG' && (
              <div>
                <hr className="my-2" />
                {card.log
                  .sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp))
                  .map((x, idx) => (
                    <div
                      key={x.id}
                      className={`d-flex align-items-center ${idx % 2 !== 0 ? 'bg-light' : ''}`}
                    >
                      <div className="bo-min-w-150">
                        <p className="bo-common-text">{moment(x.timestamp).format('YYYY-MM-DD')}</p>
                        <p className="bo-common-text">{moment(x.timestamp).format('HH:mm:ss')}</p>
                      </div>
                      <p className="bo-common-text">{x.details}</p>
                    </div>
                  ))}
              </div>
            )}
            {cardBottomOpen[card.cardId] === 'INFO' && (
              <div>
                <hr className="my-2" />
                <div className="bg-secondary p-1">
                  <p className="bo-common-text-bold bo-min-w-150 text-white">Metadata</p>
                </div>
                <div className="d-flex align-items-center">
                  <p className="bo-common-text bo-min-w-150">Card ID:</p>
                  <p className="bo-common-text text-truncate">{card.cardId}</p>
                </div>
                <div className="d-flex align-items-center">
                  <p className="bo-common-text bo-w-150">Push enabled:</p>
                  <p className="bo-common-text">
                    {card.pushEnabled ? card.pushAvailability : 'No'}
                  </p>
                </div>
                <div className="d-flex align-items-center">
                  <p className="bo-common-text bo-w-150">Pull enabled:</p>
                  <p className="bo-common-text">{card.pullEnabled ? 'Yes' : 'No'}</p>
                </div>
                <div className="d-flex align-items-center">
                  <p className="bo-common-text bo-min-w-150">Regulated:</p>
                  <p className="bo-common-text">{card.regulated ? 'True' : 'False'}</p>
                </div>
                <div className="bg-secondary p-1">
                  <p className="bo-common-text-bold bo-min-w-150 text-white">Validation</p>
                </div>
                <div className="d-flex align-items-center">
                  <p className="bo-common-text bo-w-150">Network RC:</p>
                  <p className="bo-common-text">
                    {card?.recentValidation?.networkResponseCode || '---'}
                  </p>
                </div>
                <div className="d-flex align-items-center">
                  <p className="bo-common-text bo-min-w-150">AVS ID:</p>
                  <p className="bo-common-text text-truncate">
                    {card?.recentValidation?.avsId || '---'}
                  </p>
                </div>
                <div className="d-flex align-items-center">
                  <p className="bo-common-text bo-min-w-150">AVS Code:</p>
                  <p className="bo-common-text">{card?.recentValidation?.avsResponse || '---'}</p>
                </div>
                <div className="d-flex align-items-center">
                  <p className="bo-common-text bo-min-w-150">AVS Security Code:</p>
                  <p className="bo-common-text">
                    {card?.recentValidation?.secutiryCodeResponse || '---'}
                  </p>
                </div>
                <div className="d-flex align-items-center">
                  <p className="bo-common-text bo-min-w-150">ANI Match Code:</p>
                  <p className="bo-common-text">{card?.recentValidation?.aniMatchCode || '---'}</p>
                </div>
                <div className="d-flex align-items-center">
                  <p className="bo-common-text bo-min-w-150">ANI FN Match Code:</p>
                  <p className="bo-common-text">
                    {card?.recentValidation?.aniFullNameMatchCode || '---'}
                  </p>
                </div>
                <div className="bg-secondary p-1">
                  <p className="bo-common-text-bold bo-min-w-150 text-white">Verification</p>
                </div>
                <div className="d-flex align-items-center">
                  <p className="bo-common-text bo-min-w-150">Push Status:</p>
                  <p className="bo-common-text text-truncate">
                    {card?.currentVerification?.pushStatus || '---'}
                  </p>
                </div>
                <div className="d-flex align-items-center">
                  <p className="bo-common-text bo-min-w-150">Push NRC:</p>
                  <p className="bo-common-text">{card?.currentVerification?.pushNrc || '---'}</p>
                </div>
                <div className="d-flex align-items-center">
                  <p className="bo-common-text bo-min-w-150">Pull Status:</p>
                  <p className="bo-common-text text-truncate">
                    {card?.currentVerification?.pullStatus || '---'}
                  </p>
                </div>
                <div className="d-flex align-items-center">
                  <p className="bo-common-text bo-min-w-150">Pull NRC:</p>
                  <p className="bo-common-text">{card?.currentVerification?.pullNrc || '---'}</p>
                </div>
              </div>
            )}
          </Card>
        ))}
      </div>
    </div>
  );
}

export default ConsumerTabaPayCardsSection;
